import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

/**
 * Read-access audit trail (BRD §8). One row per Customer-360 open, queue
 * expansion, or PII export — fed by the backend's PII_ACCESS_AUDIT_VIEW
 * endpoint at GET /v1/audit/pii-access. 18-month retention; used to answer
 * "who looked at this customer, when, and why" for DPDP §11 regulator
 * requests.
 */
export type PiiAccessKind = 'VIEW' | 'LIST' | 'EXPORT'

export type PiiAggregateType =
  | 'Case'
  | 'Customer'
  | 'AuditEvent'
  | 'ErasureRequest'

export interface PiiAccessRecord {
  readId: string
  accessedAt: string
  actorUserId: string | null
  actorEmpId: string | null
  actorRoles: string[]
  requestId: string | null
  accessKind: PiiAccessKind
  aggregateType: string
  aggregateId: string | null
  route: string
  rowsReturned: number | null
  bytesReturned: number | null
  clientIp: string | null
  userAgent: string | null
  contextJson: string
}

export interface PiiAccessQuery {
  from?: string
  to?: string
  actor?: string
  aggregateType?: PiiAggregateType | ''
  aggregateId?: string
  kind?: PiiAccessKind | ''
  limit?: number
  offset?: number
}

function filterParams(query: PiiAccessQuery): Record<string, string> {
  const params: Record<string, string> = {}
  if (query.from) params.from = query.from
  if (query.to) params.to = query.to
  if (query.actor) params.actor = query.actor
  if (query.aggregateType) params.aggregateType = query.aggregateType
  if (query.aggregateId) params.aggregateId = query.aggregateId
  if (query.kind) params.kind = query.kind
  return params
}

export function usePiiAccessReads(query: PiiAccessQuery) {
  return useQuery({
    queryKey: ['audit', 'pii-access', query],
    queryFn: async () => {
      const params = { ...filterParams(query), limit: query.limit ?? 200, offset: query.offset ?? 0 }
      const res = await api.get<PiiAccessRecord[]>('/v1/audit/pii-access', { params })
      return res.data
    },
    // Keep the current page on screen while the next one loads.
    placeholderData: keepPreviousData,
  })
}

/** How many reads the filters match, for "page 2 of 14"; limit/offset ignored. */
export function usePiiAccessCount(query: PiiAccessQuery) {
  const { limit: _limit, offset: _offset, ...filters } = query
  return useQuery({
    queryKey: ['audit', 'pii-access', 'count', filters],
    queryFn: async () =>
      (await api.get<{ total: number }>('/v1/audit/pii-access/count', { params: filterParams(filters) }))
        .data.total,
    placeholderData: keepPreviousData,
  })
}
